import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

const CourseContentContext = createContext();

export const useCourseContent = () => {
  const context = useContext(CourseContentContext);
  if (!context) {
    throw new Error('useCourseContent must be used within a CourseContentProvider');
  }
  return context;
};

export const CourseContentProvider = ({ children }) => {
  const [courseContents, setCourseContents] = useState({});
  // progress[courseId] = { [lessonId]: true }
  const [lessonProgress, setLessonProgress] = useState({});
  // courseProgressServer[courseId] = payload renvoyé par /api/courses/:id/progress
  const [courseProgressServer, setCourseProgressServer] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Chargement ────────────────────────────────────────────────────────────

  const loadCourseContents = useCallback(async (courseId) => {
    if (!courseId || courseContents[courseId]) return;
    setLoading(true);
    setError(null);
    try {
      const [modulesRes, courseRes] = await Promise.all([
        axios.get(`/api/modules/course/${courseId}`),
        axios.get(`/api/courses/${courseId}`).catch(() => ({ data: null })),
      ]);
      const course = courseRes.data?.course || {};
      setCourseContents(prev => ({
        ...prev,
        [courseId]: {
          courseId,
          title:       course.title       || '',
          description: course.description || '',
          modules:     modulesRes.data.modules || [],
        },
      }));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [courseContents]);

  const reloadCourseContents = useCallback(async (courseId) => {
    if (!courseId) return;
    setLoading(true);
    setError(null);
    try {
      const [modulesRes, courseRes] = await Promise.all([
        axios.get(`/api/modules/course/${courseId}`),
        axios.get(`/api/courses/${courseId}`).catch(() => ({ data: null })),
      ]);
      const course = courseRes.data?.course || {};
      setCourseContents(prev => ({
        ...prev,
        [courseId]: {
          courseId,
          title:       course.title       || '',
          description: course.description || '',
          modules:     modulesRes.data.modules || [],
        },
      }));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Progression côté serveur (leçons + évals + certif) ──────────────────
  const loadCourseProgress = useCallback(async (courseId) => {
    if (!courseId) return null;
    try {
      const { data } = await axios.get(`/api/courses/${courseId}/progress`);
      if (data?.progress) {
        setCourseProgressServer(prev => ({ ...prev, [courseId]: data.progress }));

        // Hydrater le cache local de lesson_progress
        // On n'a pas la liste des lesson_ids ici, mais on peut préserver
        // celui déjà en cache. Le détail viendra des endpoints lesson/complete.
        return data.progress;
      }
      return null;
    } catch (err) {
      console.error('[loadCourseProgress]', err.message);
      return null;
    }
  }, []);

  const getCourseProgressServer = useCallback((courseId) => {
    return courseProgressServer[courseId] || null;
  }, [courseProgressServer]);

  // ── Accesseurs ────────────────────────────────────────────────────────────

  const getCourseContent = (courseId) => courseContents[courseId] || null;

  const getModule = (courseId, moduleId) => {
    const content = courseContents[courseId];
    if (!content) return null;
    return content.modules.find(m => String(m.id) === String(moduleId)) || null;
  };

  const getLesson = (courseId, moduleId, lessonId) => {
    const mod = getModule(courseId, moduleId);
    if (!mod) return null;
    return (mod.lessons || []).find(l => String(l.id) === String(lessonId)) || null;
  };

  // ── Progression (locale, dérivée du cache) ───────────────────────────────

  /**
   * Retourne { completed, total, percentage } pour un cours.
   * Compte toutes les leçons publiées du cours.
   * Si un snapshot serveur est disponible, il prend le dessus.
   */
  const getCourseProgress = (courseId) => {
    const server = courseProgressServer[courseId];
    if (server) {
      return {
        completed: server.lessons_completed,
        total: server.lessons_total,
        percentage: server.overall_percentage,
      };
    }
    const content = courseContents[courseId];
    if (!content) return { completed: 0, total: 0, percentage: 0 };
    const progress = lessonProgress[courseId] || {};
    let total = 0;
    let completed = 0;
    (content.modules || []).forEach(mod => {
      (mod.lessons || []).forEach(lesson => {
        total++;
        if (progress[lesson.id]) completed++;
      });
    });
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  };

  // ── Complétion d'une leçon ────────────────────────────────────────────────

  const markLessonCompleted = async (courseId, moduleId, lessonId) => {
    // Optimistic update
    setLessonProgress(prev => ({
      ...prev,
      [courseId]: { ...(prev[courseId] || {}), [lessonId]: true },
    }));
    try {
      const { data } = await axios.post(`/api/lessons/${lessonId}/complete`);
      // Rafraîchir la progression serveur après complétion
      loadCourseProgress(courseId);
      return { success: true, progress: data.progress };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    }
  };

  /** Alias camelCase pour compatibilité */
  const markLessonComplete = markLessonCompleted;

  const value = {
    courseContents,
    loading,
    error,
    loadCourseContents,
    reloadCourseContents,
    loadCourseProgress,
    getCourseProgressServer,
    getCourseContent,
    getModule,
    getLesson,
    getCourseProgress,
    markLessonCompleted,
    markLessonComplete,
  };

  return (
    <CourseContentContext.Provider value={value}>
      {children}
    </CourseContentContext.Provider>
  );
};
